import { useState } from 'react';
import { Button } from '~/primitives/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '~/primitives/dialog';
import { RadioGroup, RadioGroupItem } from '~/primitives/radiogroup';
import { ScrollArea } from '~/primitives/scroll-area';
import { Checkbox } from '~/primitives/checkbox';
import PackLevelsSelector from './-PackLevelsSelector';
import { ArrowDownAZ, ArrowUpZA } from 'lucide-react';
import { getRouteApi } from '@tanstack/react-router';

const Route = getRouteApi('/packs');

interface PacksFilterProps {
    isOpen: boolean;
    onClose: () => void;
}

const PackFiltersModal: React.FC<PacksFilterProps> = ({ isOpen, onClose }) => {
    const searchParams = Route.useSearch();
    const navigate = Route.useNavigate();
    const [localSearchParams, setLocalSearchParams] = useState(searchParams);

    const applyFilters = () => {
        navigate({
            from: '/packs/$packid',
            search: { ...localSearchParams, search: '' },
            replace: true,
        });
        onClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Packs Search Settings</DialogTitle>
                    <DialogDescription>
                        Sort and filter options for packs.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 md:grid-cols-6">
                    <div className="md:col-span-2">
                        <h3
                            id="sort-label"
                            className="text-xl font-semibold mb-2"
                        >
                            Sort Type{' '}
                            {localSearchParams.reverse ? (
                                <ArrowUpZA
                                    className="inline text-secondary-light hover:text-secondary-foreground cursor-pointer transition-colors"
                                    onClick={() =>
                                        setLocalSearchParams((prev) => ({
                                            ...prev,
                                            reverse: !prev.reverse,
                                        }))
                                    }
                                />
                            ) : (
                                <ArrowDownAZ
                                    className="inline text-secondary-light hover:text-secondary-foreground cursor-pointer transition-colors"
                                    onClick={() =>
                                        setLocalSearchParams((prev) => ({
                                            ...prev,
                                            reverse: !prev.reverse,
                                        }))
                                    }
                                />
                            )}
                        </h3>
                        <RadioGroup
                            value={localSearchParams.sort}
                            onValueChange={(value: 'name' | 'points') =>
                                setLocalSearchParams((prev) => ({
                                    ...prev,
                                    sort: value,
                                }))
                            }
                            aria-labelledby="sort-label"
                            className="bg-background-dark lg:h-[30vh] p-4 rounded-xl"
                        >
                            <RadioGroupItem
                                value="name"
                                id="name"
                                label="Pack Name"
                            />
                            <RadioGroupItem
                                value="points"
                                id="points"
                                label="Points awarded"
                            />
                        </RadioGroup>
                    </div>

                    <div className="md:col-span-2">
                        <h3 className="text-xl font-semibold mb-2 ">Filters</h3>

                        <div className="mb-4 bg-background-dark p-2 rounded-xl lg:h-[30vh]">
                            <ScrollArea className="h-full">
                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        checked={
                                            localSearchParams.filters
                                                .progress_only
                                        }
                                        id="progress"
                                        onCheckedChange={(value) =>
                                            setLocalSearchParams((prev) => ({
                                                ...prev,
                                                filters: {
                                                    ...prev.filters,
                                                    progress_only:
                                                        value as boolean,
                                                },
                                            }))
                                        }
                                    />
                                    <label htmlFor="progress">
                                        Only show packs on which you have
                                        progress
                                    </label>
                                </div>
                            </ScrollArea>
                        </div>
                    </div>

                    <div className="md:col-span-2">
                        <h3 className="text-xl font-semibold mb-2">
                            Include/Exclude Levels
                        </h3>
                        <PackLevelsSelector
                            levelStates={localSearchParams.filters.levels}
                            setLevelStates={(levels) =>
                                setLocalSearchParams((prev) => ({
                                    ...prev,
                                    filters: {
                                        ...prev.filters,
                                        levels,
                                    },
                                }))
                            }
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button onClick={applyFilters}>Apply</Button>
                    <Button variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default PackFiltersModal;
